import React from "react";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Button,
  Box,
} from "@mui/material";
import { Add, DeleteOutline } from "@mui/icons-material";

const LiabilityTypes = ({ formData, setFormData, setIsChanged }: any) => {
  const tableHeadings = [
    { label: "Dropdown Text", align: "center" },
    { label: "Description", align: "center" },
    { label: "Percent or Value", align: "center" },
    { label: "Action", align: "center" },
  ];

  const smallInputSX = {
    "& .MuiInputBase-input": {
      padding: "2px 6px !important",
      fontSize: "14px",
      height: "30px",
      boxSizing: "border-box",
    },
  };

  const smallSelectSX = {
    "& select": {
      padding: "2px 6px !important",
      fontSize: "14px",
      height: "26px",
    },
  };

  // ✅ FIXED: Auto assign type from API
  const LiabilityType =
    formData?.insurance_extended_liability_types?.length > 0
      ? formData.insurance_extended_liability_types.map((item: any) => ({
          ...item,
          type: item.type || "percent", // Use actual type property, default to percent
        }))
      : [
          {
            name: "",
            description: "",
            percent: null,
            value: null,
            type: "percent",
            is_editable: true,
            is_default: false,
            is_deletable: true,
            is_delete: false,
          },
        ];

  const handleInputChange = (
    index: number,
    field: string,
    value: string | boolean,
  ) => {
    const updated = LiabilityType.map((item: any) => ({ ...item }));
    updated[index][field] = value;

    setFormData((prev: any) => ({
      ...prev,
      insurance_extended_liability_types: updated,
    }));

    setIsChanged(true);
  };

  const handleAddRow = () => {
    const updated = [
      ...LiabilityType,
      {
        name: "",
        description: "",
        percent: null,
        value: null,
        type: "percent",
        is_editable: true,
        is_deletable: true,
        is_default: false,
        is_delete: false,
      },
    ];

    setFormData((prev: any) => ({
      ...prev,
      insurance_extended_liability_types: updated,
    }));
    setIsChanged(true);
  };

  const handleDelete = (index: number) => {
    const updated = LiabilityType.filter((_: any, i: number) => i !== index);

    if (updated.length === 0)
      updated.push({
        name: "",
        description: "",
        percent: null,
        value: null,
        type: "percent",
        is_editable: true,
        is_deletable: true,
        is_default: false,
        is_delete: false,
      });

    setFormData((prev: any) => ({
      ...prev,
      insurance_extended_liability_types: updated,
    }));

    setIsChanged(true);
  };

  return (
    <Grid item xs={12} md={12}>
      <Paper
        sx={{
          p: 2,
          borderRadius: "16px",
          backgroundColor: "#f8f9fa",
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        }}
      >
        <Typography
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            color: "#02906F",
            mb: 2,
            fontSize: "1rem",
          }}
        >
          Insurance-Extended Liability Types
        </Typography>

        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "12px",
            overflowX: "auto",
            maxHeight: 350,
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background: "#02906F",
                  "& th": {
                    color: "#fff",
                    fontWeight: 600,
                    textAlign: "center",
                    padding: "10px",
                  },
                }}
              >
                {tableHeadings.map((h, i) => (
                  <TableCell key={i}>{h.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {LiabilityType.map((row: any, index: number) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:hover": { backgroundColor: "#e9f6f3" },
                    transition: "background 0.3s",
                  }}
                >
                  {/* Dropdown Text */}
                  <TableCell sx={{ textAlign: "center", padding: "4px 16px" }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={row.name}
                      disabled={!row?.is_editable}
                      onChange={(e) =>
                        handleInputChange(index, "name", e.target.value)
                      }
                      placeholder="Enter dropdown text"
                      sx={smallInputSX}
                    />
                  </TableCell>

                  {/* Description */}
                  <TableCell
                    sx={{
                      textAlign: "center",
                      padding: "4px 6px",
                      width: "40%",
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={row.description}
                      disabled={!row?.is_editable}
                      onChange={(e) =>
                        handleInputChange(index, "description", e.target.value)
                      }
                      placeholder="Enter description"
                      sx={smallInputSX}
                    />
                  </TableCell>

                  {/* Percent or Value */}
                  <TableCell sx={{ textAlign: "center", padding: "4px 6px" }}>
                    {row?.type === "percent" && (
                      <TextField
                        size="small"
                        placeholder="% Percent"
                        value={row.percent}
                        type="text"
                        disabled={!row?.is_editable || row.type !== "percent"}
                        onChange={(e) => {
                          const value = e.target.value;

                          // allow only numbers with max 2 decimal
                          if (/^\d{0,3}(\.\d{0,2})?$/.test(value)) {
                            if (parseFloat(value) <= 100 || value === "") {
                              handleInputChange(index, "percent", value);
                            }
                          }
                        }}
                        inputProps={{ maxLength: 6 }}
                        sx={{ width: "120px", ...smallInputSX, mr: 1 }}
                      />
                    )}

                    {row?.type === "value" && (
                      <TextField
                        size="small"
                        placeholder="£ Amount"
                        value={row.value}
                        type="text"
                        disabled={!row?.is_editable || row.type !== "value"}
                        onChange={(e) => {
                          const value = e.target.value;

                          // max 3 digits before decimal and 2 after
                          if (/^\d{0,3}(\.\d{0,2})?$/.test(value)) {
                            handleInputChange(index, "value", value);
                          }
                        }}
                        inputProps={{ maxLength: 6 }}
                        sx={{ width: "120px", ...smallInputSX }}
                      />
                    )}
                    <TextField
                      select
                      size="small"
                      value={row.type || "percent"}
                      onChange={(e) => {
                        const selected = e.target.value;

                        const updated = LiabilityType.map(
                          (item: any, i: number) => {
                            if (i === index) {
                              return {
                                ...item,
                                type: selected,
                                // Do not clear percent or value, just switch the type
                              };
                            }
                            return item;
                          },
                        );

                        setFormData((prev: any) => ({
                          ...prev,
                          insurance_extended_liability_types: updated,
                        }));
                        setIsChanged(true);
                      }}
                      SelectProps={{ native: true }}
                      sx={{ width: "100px", ml: 1, ...smallSelectSX }}
                    >
                      <option value="percent">Percent</option>
                      <option value="value">Amount</option>
                    </TextField>
                  </TableCell>

                  {/* Action */}
                  <TableCell sx={{ textAlign: "center", padding: "4px 16px" }}>
                    {row?.is_deletable && (
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(index)}
                      >
                        <DeleteOutline />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add Row Button */}
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAddRow}
            sx={{
              textTransform: "capitalize",
              fontWeight: 500,
              px: 3,
              py: 0.5,
              borderRadius: "8px",
            }}
          >
            Add More
          </Button>
        </Box>
      </Paper>
    </Grid>
  );
};

export default LiabilityTypes;
